<template>
    <div class="page-container">
      <div class="card-wrapper">
        
        <!-- Top Hero Banner Card -->
        <header class="hero-banner">
          <div class="banner-content">
            <h1 class="banner-title">Tenancy Agreements Status</h1>
            <p class="banner-desc">
              Ensure all active units have signed and verified agreements for the 2024 fiscal year.
            </p>
          </div>
          <!-- Absolute Layer Watermark Document Icon -->
          <div class="banner-watermark-icon">
            <svg viewBox="0 0 24 24">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
            </svg>
          </div>
        </header>
  
        <!-- Units Counts Section Header Row -->
        <section class="units-header-row">
          <h2 class="section-label">Active Units</h2>
          <div class="units-badge">
            <span class="badge-text">6 Units Total</span>
          </div>
        </section>
  
        <!-- Unit Cards List Collection -->
        <main class="units-list">
          <article 
            v-for="(unit, index) in units" 
            :key="index" 
            class="unit-card"
          >
            <!-- Left Rounded Icon Container Block -->
            <div class="building-icon-wrapper">
              <!-- Building Property SVG Line Icon -->
              <svg viewBox="0 0 24 24" class="svg-building">
                <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
              </svg>
            </div>
  
            <!-- Right Content Metrics Block -->
            <div class="unit-card-content">
              <h3 class="unit-title">{{ unit.name }} - {{ unit.type }}</h3>
              <p class="tenant-name">Tenant: {{ unit.tenant }}</p>
              
              <!-- Warning / Alert Warning Pending Label Row -->
              <div class="status-warning-row">
                <svg viewBox="0 0 24 24" class="svg-warning-icon">
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
                <span class="status-label-text">Pending</span>
              </div>
            </div>
          </article>
        </main>
  
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'TenancyAgreementsStatus',
    data() {
      return {
        units: [
          {
            name: 'Unit 1',
            type: '2 Bedroom',
            tenant: 'Sarah Jenkins'
          },
          {
            name: 'Unit 3',
            type: '1 Bedroom',
            tenant: 'David Wilson'
          },
          {
            name: 'Unit 3',
            type: '1 Bedroom',
            tenant: 'David Wilson'
          }
        ]
      };
    }
  };
  </script>
  
  <style lang="scss" scoped>
  // Design Tokens Variables Setup
  $color-bg-page: #fbfcfe;
  $color-brand-blue-gradient-start: #0060ff;
  $color-brand-blue-gradient-end: #003cd2;
  $color-badge-bg: #eceffd;
  $color-badge-text: #2c42b7;
  $color-card-bg: #ffffff;
  $color-text-dark: #121826;
  $color-text-muted: #535d72;
  $color-warning-amber: #f59e0b;
  
  .page-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: $color-bg-page;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    padding: 32px 16px;
    box-sizing: border-box;
  
    .card-wrapper {
      width: 100%;
      max-width: 480px;
      display: flex;
      flex-direction: column;
      background: #f8faff;
      box-shadow: 
        0 0 0 1px rgba(0, 0, 0, 0.05), 
        0 4px 12px rgba(0, 0, 0, 0.05), 
        0 12px 24px rgba(0, 0, 0, 0.05);
        padding: 15px;
        border-radius: 6px;
    }
  
    // 1. Top Hero Banner Card Layout
    .hero-banner {
      position: relative;
      background: linear-gradient(135deg, $color-brand-blue-gradient-start, $color-brand-blue-gradient-end);
      border-radius: 20px;
      padding: 28px 24px;
      box-shadow: 0 12px 30px rgba(0, 75, 240, 0.16);
      overflow: hidden;
      margin-bottom: 32px;
  
      .banner-content {
        position: relative;
        z-index: 2;
        max-width: 82%;
      }
  
      .banner-title {
        font-size: 26px;
        font-weight: 700;
        color: #ffffff;
        margin: 0 0 14px 0;
        letter-spacing: -0.4px;
      }
  
      .banner-desc {
        font-size: 14.5px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.88);
        margin: 0;
        font-weight: 400;
      }
  
      // Absolute Background Watermark Vector Asset
      .banner-watermark-icon {
        position: absolute;
        right: -8px;
        bottom: -4px;
        width: 110px;
        height: 110px;
        opacity: 0.12;
        z-index: 1;
        pointer-events: none;
  
        svg {
          width: 100%;
          height: 100%;
          fill: #ffffff;
        }
      }
    }
  
    // 2. Units Meta Header Summary Row
    .units-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 18px;
      padding: 0 4px;
  
      .section-label {
        font-size: 18px;
        font-weight: 600;
        color: $color-text-muted;
        margin: 0;
      }
  
      .units-badge {
        background-color: $color-badge-bg;
        padding: 6px 14px;
        border-radius: 10px;
  
        .badge-text {
          font-size: 15px;
          font-weight: 600;
          color: $color-badge-text;
        }
      }
    }
  
    // 3. Main Stream Stacked Collection Layout
    .units-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
  
      .unit-card {
        background-color: $color-card-bg;
        border-radius: 20px;
        padding: 22px;
        display: flex;
        align-items: flex-start;
        gap: 18px;
        box-shadow: 0 2px 8px rgba(0, 40, 110, 0.01), 0 10px 24px rgba(0, 40, 110, 0.025);
        box-sizing: border-box;
        transition: transform 0.2s ease;
  
        &:hover {
          transform: translateY(-1px);
        }
  
        // Left Graphical Square Badge
        .building-icon-wrapper {
          background-color: #eef2ff;
          border-radius: 12px;
          width: 46px;
          height: 46px;
          min-width: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
  
          .svg-building {
            width: 22px;
            height: 22px;
            fill: #0c1a4b;
          }
        }
  
        // Main Text Metrics Array Block
        .unit-card-content {
          display: flex;
          flex-direction: column;
  
          .unit-title {
            font-size: 17px;
            font-weight: 600;
            color: $color-text-dark;
            margin: 0 0 6px 0;
            letter-spacing: -0.1px;
          }
  
          .tenant-name {
            font-size: 15px;
            color: $color-text-muted;
            margin: 0 0 12px 0;
            font-weight: 500;
          }
  
          // Warning Label Set UI
          .status-warning-row {
            display: flex;
            align-items: center;
            gap: 6px;
  
            .svg-warning-icon {
              width: 16px;
              height: 16px;
              fill: $color-warning-amber;
            }
  
            .status-label-text {
              font-size: 15px;
              font-weight: 600;
              color: $color-warning-amber;
              letter-spacing: 0.2px;
            }
          }
        }
      }
    }
  }
  
  // Media Adaptive Screen Breakpoints Config
  @media (max-width: 400px) {
    .page-container {
      padding: 20px 12px;
  
      .hero-banner {
        padding: 22px 18px;
        margin-bottom: 24px;
        
        .banner-content { max-width: 100%; }
        .banner-title { font-size: 22px; }
        .banner-desc { font-size: 13.5px; }
        .banner-watermark-icon { display: none; }
      }
  
      .units-header-row {
        .section-label { font-size: 16px; }
        .units-badge { 
          padding: 4px 10px;
          .badge-text { font-size: 13.5px; }
        }
      }
  
      .units-list .unit-card {
        padding: 16px;
        gap: 14px;
        
        .building-icon-wrapper {
          width: 40px;
          height: 40px;
          min-width: 40px;
          border-radius: 10px;
          .svg-building { width: 18px; height: 18px; }
        }
  
        .unit-card-content {
          .unit-title { font-size: 15px; }
          .tenant-name { font-size: 13.5px; margin-bottom: 8px; }
          .status-warning-row .status-label-text { font-size: 13.5px; }
        }
      }
    }
  }
  </style>
  